/**
 * Trang thai phieu cuoc.
 *
 * Diem nghiep vu quan trong nhat: giao dien GIU LAI ty le tai thoi diem nguoi choi
 * bam chon (`capturedOddsBp`) va gui len may chu kem theo. May chu doi chieu voi ty le
 * cua no roi quyet dinh (SPT-R20) — giao dien khong bao gio duoc tu chot phieu bang
 * ty le minh dang hien.
 *
 * SPT-R22: ty le thay doi CO LOI cho nguoi choi thi may chu luon nhan, khong hoi lai.
 * Chi khi ty le xau di moi can hoi y kien.
 */

import {
  createContext,
  useContext,
  useMemo,
  useReducer,
  type JSX,
  type ReactNode,
} from 'react';
import type { OddsChangePolicy } from './api';

export interface BetslipLeg {
  selectionId: string;
  eventId: string;
  marketId: string;
  /** Ty le luc nguoi choi bam chon. Dung de doi chieu, khong dung de chot phieu. */
  capturedOddsBp: number;
  selectionName: string;
  marketLabel: string;
  eventLabel: string;
  isLive: boolean;
}

export type BetMode = 'SINGLE' | 'MULTI';

interface BetslipState {
  legs: BetslipLeg[];
  mode: BetMode;
  /** Tien cuoc chung cho phieu xau. */
  stake: string;
  /** Tien cuoc rieng tung chang khi dat cuoc don. */
  singleStakes: Record<string, string>;
  oddsPolicy: OddsChangePolicy;
  open: boolean;
}

type Action =
  | { type: 'TOGGLE'; leg: BetslipLeg }
  | { type: 'REMOVE'; selectionId: string }
  | { type: 'CLEAR' }
  | { type: 'SET_MODE'; mode: BetMode }
  | { type: 'SET_STAKE'; value: string }
  | { type: 'SET_SINGLE_STAKE'; selectionId: string; value: string }
  | { type: 'SET_POLICY'; policy: OddsChangePolicy }
  | { type: 'SET_OPEN'; open: boolean };

const MAX_LEGS = 20;

function reducer(state: BetslipState, action: Action): BetslipState {
  switch (action.type) {
    case 'TOGGLE': {
      const exists = state.legs.some((l) => l.selectionId === action.leg.selectionId);
      if (exists) {
        const legs = state.legs.filter((l) => l.selectionId !== action.leg.selectionId);
        return { ...state, legs, mode: legs.length < 2 ? 'SINGLE' : state.mode };
      }
      if (state.legs.length >= MAX_LEGS) return state;

      /**
       * Hai lua chon cung mot thi truong loai tru nhau: chon cai moi thi bo cai cu.
       * May chu cung chan (SPT-R10), nhung chan o day cho nguoi choi khong phai
       * bam roi moi biet la sai.
       */
      const legs = [
        ...state.legs.filter((l) => l.marketId !== action.leg.marketId),
        action.leg,
      ];
      return { ...state, legs, open: true, mode: legs.length > 1 ? state.mode : 'SINGLE' };
    }

    case 'REMOVE': {
      const legs = state.legs.filter((l) => l.selectionId !== action.selectionId);
      return { ...state, legs, mode: legs.length < 2 ? 'SINGLE' : state.mode };
    }

    case 'CLEAR':
      return { ...state, legs: [], stake: '', singleStakes: {}, open: false };

    case 'SET_MODE':
      return { ...state, mode: action.mode };

    case 'SET_STAKE':
      return { ...state, stake: action.value };

    case 'SET_SINGLE_STAKE':
      return {
        ...state,
        singleStakes: { ...state.singleStakes, [action.selectionId]: action.value },
      };

    case 'SET_POLICY':
      return { ...state, oddsPolicy: action.policy };

    case 'SET_OPEN':
      return { ...state, open: action.open };
  }
}

const initialState: BetslipState = {
  legs: [],
  mode: 'SINGLE',
  stake: '',
  singleStakes: {},
  // Mac dinh chap nhan ty le TOT LEN: khong co ly do gi hoi lai nguoi choi
  // khi thay doi co loi cho ho.
  oddsPolicy: 'ACCEPT_HIGHER',
  open: false,
};

interface BetslipValue extends BetslipState {
  toggle: (leg: BetslipLeg) => void;
  remove: (selectionId: string) => void;
  clear: () => void;
  setMode: (mode: BetMode) => void;
  setStake: (value: string) => void;
  setSingleStake: (selectionId: string, value: string) => void;
  setPolicy: (policy: OddsChangePolicy) => void;
  setOpen: (open: boolean) => void;
  has: (selectionId: string) => boolean;
  count: number;
}

const BetslipContext = createContext<BetslipValue | null>(null);

export function BetslipProvider({ children }: { children: ReactNode }): JSX.Element {
  const [state, dispatch] = useReducer(reducer, initialState);

  const value = useMemo<BetslipValue>(
    () => ({
      ...state,
      count: state.legs.length,
      toggle: (leg) => dispatch({ type: 'TOGGLE', leg }),
      remove: (selectionId) => dispatch({ type: 'REMOVE', selectionId }),
      clear: () => dispatch({ type: 'CLEAR' }),
      setMode: (mode) => dispatch({ type: 'SET_MODE', mode }),
      setStake: (value) => dispatch({ type: 'SET_STAKE', value }),
      setSingleStake: (selectionId, value) =>
        dispatch({ type: 'SET_SINGLE_STAKE', selectionId, value }),
      setPolicy: (policy) => dispatch({ type: 'SET_POLICY', policy }),
      setOpen: (open) => dispatch({ type: 'SET_OPEN', open }),
      has: (selectionId) => state.legs.some((l) => l.selectionId === selectionId),
    }),
    [state],
  );

  return <BetslipContext.Provider value={value}>{children}</BetslipContext.Provider>;
}

export function useBetslip(): BetslipValue {
  const value = useContext(BetslipContext);
  if (!value) throw new Error('useBetslip phai dung ben trong BetslipProvider');
  return value;
}

/** Ty le tong cua phieu xau: tich cac ty le, quy ve cung thang do. */
export function combineOddsBp(legs: readonly { capturedOddsBp: number }[]): bigint {
  if (legs.length === 0) return 0n;
  let product = 1n;
  for (const leg of legs) product *= BigInt(leg.capturedOddsBp);
  return product / 10_000n ** BigInt(legs.length - 1);
}

export { MAX_LEGS };
